import { useMemo, useState } from 'react';
import { usePricingCourses, type PricingCourseRow } from '../../hooks/usePricing.js';
import { activeScholarship, isNegativeNet, matchesCourseQuery, scholarshipLabel } from '../../lib/pricing.js';
import { centsToAudDisplay } from '../../lib/money.js';
import { formatQuoteDate, isAddable } from '../../lib/quotes.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';

/** A quote can hold at most 20 courses — mirrors the server's courseIds max. */
export const MAX_QUOTE_COURSES = 20;

/**
 * Pick the courses a quote will lock (Brief 7b).
 *
 * Reads the SAME endpoint the consultant pricing screen reads
 * (usePricingCourses → GET /admin/pricing/courses) — already view_pricing-gated
 * and consultant-reachable, so this screen adds ZERO new API surface. Because the
 * picker and the server's lock both flow from netFeeCents over the same
 * effective-dated resolution, the number shown here is the number that gets
 * locked — the figures can only differ if a price changes between picking and
 * saving, which is exactly what "indicative" means.
 *
 * The browser never computes fee minus scholarship; every figure is the server's.
 */
export function CoursePicker({
  pickedIds,
  onAdd,
}: {
  pickedIds: readonly string[];
  onAdd: (course: PricingCourseRow) => void;
}) {
  const { data, isLoading, error, refetch } = usePricingCourses();
  const [search, setSearch] = useState('');

  const items = useMemo(() => data?.items ?? [], [data]);
  const filtered = useMemo(
    () =>
      items
        .filter((c) => matchesCourseQuery(c, search))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [items, search],
  );

  const atLimit = pickedIds.length >= MAX_QUOTE_COURSES;

  return (
    <div>
      <Input
        placeholder="Search course or college…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* asOf is a calendar date — formatQuoteDate, never formatDate. */}
      {data?.asOf && (
        <p className="mt-2 text-xs text-muted">Prices current as of {formatQuoteDate(data.asOf)}.</p>
      )}
      {atLimit && (
        <p className="mt-2 text-xs text-muted">
          A quote can hold at most {MAX_QUOTE_COURSES} courses.
        </p>
      )}

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading courses…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn&apos;t load courses: {(error as Error).message}{' '}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No courses found</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <div className="surface p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">Course</th>
                  <th className="px-5 py-3 font-medium text-right">Fee</th>
                  <th className="px-5 py-3 font-medium text-right">Scholarship</th>
                  <th className="px-5 py-3 font-medium text-right">Net fee</th>
                  <th className="px-5 py-3 font-medium text-right">Add</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((c) => (
                  <PickerRow
                    key={c.courseId}
                    course={c}
                    picked={pickedIds.includes(c.courseId)}
                    atLimit={atLimit}
                    onAdd={onAdd}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PickerRow({
  course: c,
  picked,
  atLimit,
  onAdd,
}: {
  course: PricingCourseRow;
  picked: boolean;
  atLimit: boolean;
  onAdd: (course: PricingCourseRow) => void;
}) {
  // The API always sends both fields; `?? null` covers older cached shapes.
  const net = c.netFeeCents ?? null;
  // null / kind 'none' / malformed all mean: no scholarship in force → "—".
  const sch = activeScholarship(c.scholarship);

  return (
    <tr className="hover:bg-cream/30 transition-colors align-top">
      <td className="px-5 py-3 min-w-[16rem]">
        <span className="block text-sm text-ink">{c.name}</span>
        <span className="block text-xs text-muted">{c.providerName}</span>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {c.agencyFee ? (
          <span className="text-sm font-medium text-navy">
            {centsToAudDisplay(c.agencyFee.tuitionCents)}
          </span>
        ) : (
          <span className="badge bg-cream-deep/60 text-muted">No fee set</span>
        )}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-ink">
        {sch ? scholarshipLabel(sch) : '—'}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {net === null ? (
          <span className="text-sm text-muted">—</span>
        ) : isNegativeNet(net) ? (
          // A scholarship now exceeds the fee — shown honestly, never clamped.
          // Still addable: the SERVER adjudicates money and refuses it by name.
          <span
            data-testid="negative-net"
            className="inline-block rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger"
          >
            {centsToAudDisplay(net)}
          </span>
        ) : (
          <span className="text-sm font-medium text-navy">{centsToAudDisplay(net)}</span>
        )}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <PickerAction course={c} picked={picked} atLimit={atLimit} onAdd={onAdd} />
      </td>
    </tr>
  );
}

function PickerAction({
  course: c,
  picked,
  atLimit,
  onAdd,
}: {
  course: PricingCourseRow;
  picked: boolean;
  atLimit: boolean;
  onAdd: (course: PricingCourseRow) => void;
}) {
  // No fee ⇒ nothing to lock. The engine refuses this line outright, so it is
  // shown (the consultant should see the course exists) but never offered.
  if (!isAddable(c)) {
    return <span className="text-xs text-muted">No fee set — ask the pricing team</span>;
  }
  // Already on the quote. A duplicate courseId is a 400; reaching that would be a
  // UI bug, so the affordance is simply gone.
  if (picked) {
    return <span className="badge bg-cream-deep/60 text-muted">Added</span>;
  }
  return (
    <Button size="sm" variant="secondary" disabled={atLimit} onClick={() => onAdd(c)}>
      Add
    </Button>
  );
}
